import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

interface UseKeyboardNavigationProps<T> {
  items: T[];
  selectedItemId: number | null;
  getItemId: (item: T) => number;
  onItemSelect: (itemId: number) => void;
  selectorName?: string;
}

const useKeyboardNavigation = <T,>({
  items,
  selectedItemId,
  getItemId,
  onItemSelect,
  selectorName = 'rowid'
}: UseKeyboardNavigationProps<T>) => {
  const isMobile = useSelector(select.device.isMobile);

  if (isMobile) {
    return;
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!selectedItemId) return;

      const selectedIndex = items.findIndex((item) => getItemId(item) === selectedItemId);
      if (selectedIndex === -1) return;

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          event.preventDefault();

          if (selectedIndex < items.length - 1) {
            onItemSelect(getItemId(items[selectedIndex + 1]));
          }
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          event.preventDefault();

          if (selectedIndex > 0) {
            onItemSelect(getItemId(items[selectedIndex - 1]));
          }
          break;
        default:
          break;
      }

      setTimeout(() => {
        const element = document.querySelector(`[data-${selectorName}="${selectedItemId}"]`);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 50);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [items, selectedItemId, getItemId, onItemSelect, selectorName]);
};

export default useKeyboardNavigation;
